/**
 * How Do I Get Started? Section — Home Page
 *
 * Four-step numbered guide walking customers through the booking process.
 * Replaces the previous "Why Choose Us" trust-signal cards.
 * CMS-ready: content fetched from Backoffice when configured, falls back to defaults.
 */

import { usePageSection } from '@/hooks/usePageContent';
import { cn } from '@/lib/utils';

interface GettingStartedStep {
    title: string;
    description: string;
}

export interface WhyChooseUsContent {
    heading: string;
    steps: GettingStartedStep[];
}

const DEFAULT_GETTING_STARTED: WhyChooseUsContent = {
    heading: 'How Do I Get Started?',
    steps: [
        {
            title: 'Select your location and dates',
            description:
                'Choose the location you need the equipment at. If you already know your dates, enter them now — or leave them open for a flexible rental.',
        },
        {
            title: 'Browse and choose your equipment',
            description:
                'Use filters or browse the full product range. Click on any item to see details, pricing, and availability.',
        },
        {
            title: 'Choose your rental period and add to cart',
            description:
                'Select the rental duration that suits you — short-term with fixed dates, or a rolling monthly rental — then add the item to your cart.',
        },
        {
            title: 'Checkout and pay',
            description:
                'For short-term rentals, you pay the full amount at checkout. For monthly rentals, an initial payment covers the first month — after that, a monthly charge applies until the item is returned.',
        },
    ],
};

/** Alternating step number colours — teal / coral */
const STEP_COLORS = ['text-primary', 'text-highlight', 'text-primary', 'text-highlight'] as const;

export const WhyChooseUsSection = () => {
    const cms = usePageSection<WhyChooseUsContent>('home', 'why-choose-us');
    const content = cms ?? DEFAULT_GETTING_STARTED;

    return (
        <section
            aria-labelledby="getting-started-heading"
            className="bg-white py-16 sm:py-24"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

                {/* Section heading */}
                <h2
                    id="getting-started-heading"
                    className="delight-rise mb-12 font-display text-5xl leading-[0.94] tracking-tight text-foreground sm:text-6xl sm:mb-16"
                >
                    {content.heading}
                </h2>

                {/* Step cards */}
                <ol className="delight-rise delight-delay-1 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {content.steps.map((step, index) => (
                        <li
                            key={index}
                            data-testid="getting-started-card"
                            className="flex flex-col rounded-xl border border-border bg-white p-6 transition-[transform,box-shadow,border-color] duration-200 hover:border-primary/20 hover:shadow-[0_18px_34px_rgba(54,49,43,0.08)] motion-safe:hover:-translate-y-1"
                        >
                            <span
                                className={cn(
                                    'select-none font-display text-5xl leading-none',
                                    STEP_COLORS[index % STEP_COLORS.length]
                                )}
                                aria-hidden="true"
                            >
                                {index + 1}
                            </span>
                            <h3 className="mt-5 font-display text-xl leading-tight text-foreground sm:text-2xl">
                                <span className="sr-only">Step {index + 1}: </span>
                                {step.title}
                            </h3>
                            <p className="mt-2 text-sm leading-7 text-muted-foreground sm:text-base">
                                {step.description}
                            </p>
                        </li>
                    ))}
                </ol>

            </div>
        </section>
    );
};

export default WhyChooseUsSection;
